import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SearchSelect from '../components/shared/SearchSelect';
import { Field, FilePicker, inputCls } from '../components/shared/FormKit';
import { FormValidationProvider, useFormValidation } from '../components/shared/formValidation';
import { FormShell, FormSection } from '../components/workflow/FormShell';
import ReviewerSelect, { MASTER_REVIEWER_HINT } from '../components/workflow/ReviewerSelect';
import SegmentedSwitch from '../components/shared/SegmentedSwitch';
import { formatDate } from '../utils/dateFormat';
import { firmToday } from '../utils/dateRange';
import { fetchJSON } from '../utils/api';

// Client Amendment — submission form (docs/modules/workflow-client.md §7).
// Pick a registered entity; its name and client group name are prefilled with
// the CURRENT values so the requester edits in place — only the fields that
// differ are sent. UEN is shown, not edited; UEN, Date of Appointment,
// Appointment Letters and group structure charts are changed in Management
// only. Supporting attachments are optional. Moving the entity to ANOTHER existing
// group is deferred by design (backend payload mechanism exists).
const WorkflowCarNew = () => {
  const navigate = useNavigate();

  const [entities, setEntities] = useState([]);
  const [entityId, setEntityId] = useState('');
  const [entityName, setEntityName] = useState('');
  // Relationship: 'active' | 'inactive' — marking inactive takes a date (not in the
  // future) and the amendment's Reason doubles as the inactive reason.
  const [relationship, setRelationship] = useState('active');
  const [inactiveDate, setInactiveDate] = useState(firmToday());
  const [groupName, setGroupName] = useState('');
  const [files, setFiles] = useState([]);
  const [reason, setReason] = useState('');
  const [reviewerStaffId, setReviewerStaffId] = useState('');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const pickedEntity = entities.find((x) => String(x.id) === String(entityId)) || null;

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchJSON('/api/workflow/car/options');
        setEntities(data.entities || []);
      } catch (e) { setError(e.message); }
    })();
  }, []);

  useEffect(() => {
    // The picked entity's current values prefill the editable fields so the
    // requester changes only what actually changes.
    const picked = entities.find((x) => String(x.id) === String(entityId)) || null;
    setEntityName(picked?.entityName || '');
    setRelationship(picked && picked.active === false ? 'inactive' : 'active');
    setInactiveDate(firmToday());
    setGroupName(picked?.clientGroupName || '');
  }, [entityId, entities]);

  // A field counts as a change only when it differs from the current value.
  const currentGroupName = pickedEntity?.clientGroupName || '';
  const currentRelationship = pickedEntity && pickedEntity.active === false ? 'inactive' : 'active';
  const nameTo = entityName.trim() && entityName.trim() !== (pickedEntity?.entityName || '') ? entityName.trim() : '';
  const relationshipTo = pickedEntity && relationship !== currentRelationship ? relationship : '';
  const renameTo = groupName.trim() && groupName.trim() !== currentGroupName ? groupName.trim() : '';
  const hasFieldChange = !!(nameTo || relationshipTo || renameTo);
  // Marking a company inactive that still has an engagement in progress is refused by
  // the server — say so under the switch and hold the Submit.
  const inProgress = pickedEntity?.inProgressEngagementCount || 0;
  const inactiveBlocked = relationshipTo === 'inactive' && inProgress > 0;
  const inactiveDateError = relationshipTo === 'inactive' && inactiveDate && inactiveDate > firmToday() ? 'The inactive date cannot be in the future.' : null;

  // The two blanks this form can have — the entity and the reason. "Something
  // to amend" is a rule across optional slots and stays a banner error.
  const gaps = useMemo(() => {
    const g = [];
    if (!entityId) g.push({ key: 'entityId', label: 'Client Entity' });
    if (!reason.trim()) g.push({ key: 'reason', label: 'Reason' });
    if (!reviewerStaffId) g.push({ key: 'reviewerName', label: 'Reviewer' });
    return g;
  }, [entityId, reason, reviewerStaffId]);
  const validation = useFormValidation(gaps);

  const submit = async () => {
    setError(null);
    if (!validation.validate()) return;
    if (!hasFieldChange) {
      setError('Nothing to amend — change the entity name, relationship or client group name.');
      return;
    }
    if (inactiveBlocked) { setError('This company still has engagements in progress — close them before marking the company inactive.'); return; }
    if (inactiveDateError) { setError(inactiveDateError); return; }
    setSaving(true);
    try {
      const body = new FormData();
      body.set('entityId', parseInt(entityId, 10));
      body.set('reason', reason.trim());
      body.set('reviewerStaffId', reviewerStaffId);
      if (nameTo) body.set('entityName', nameTo);
      if (relationshipTo) {
        body.set('relationship', relationshipTo);
        if (relationshipTo === 'inactive') body.set('inactiveDate', inactiveDate);
      }
      if (renameTo) body.set('groupName', renameTo);
      files.forEach((x) => body.append('files', x));
      const data = await fetchJSON('/api/workflow/car', { method: 'POST', body });
      navigate(`/workflow/car/${data.request.id}`);
    } catch (e) { setError(e.message); setSaving(false); }
  };

  return (
    <FormShell
      backTo="/workflow/car"
      backLabel="Client Amendment"
      title="New Client Amendment Request"
      subtitle="Update a registered client's details."
      error={error || validation.error}
      onSubmit={submit}
      submitting={saving}
      submitDisabled={inactiveBlocked}
    >
      <FormValidationProvider value={validation.providerValue}>
        <FormSection step={1} title="Select the Client Entity">
          <Field label="Client Entity" required field="entityId">
            <SearchSelect
              value={entityId}
              onChange={setEntityId}
              options={entities.map((en) => ({ value: en.id, label: `${en.entityName} (${en.uen})${en.clientGroupName ? ` · ${en.clientGroupName}` : ''}${en.active === false ? ' · INACTIVE' : ''}` }))}
              placeholder="Search an entity…"
            />
          </Field>
        </FormSection>

        <FormSection step={2} title="Client Details" hint="Prefilled with the current values — edit only what changed. Only the changed fields go on the request.">
          {entityId ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Entity Name" hint={nameTo ? `Currently "${pickedEntity?.entityName}"` : undefined}>
                <input
                  type="text"
                  value={entityName}
                  onChange={(e) => setEntityName(e.target.value)}
                  className={inputCls}
                />
              </Field>
              <Field label="UEN">
                <p className="text-sm text-slate-700 font-mono py-2">{pickedEntity?.uen || '—'}</p>
              </Field>
              <div className="md:col-span-2">
                <Field
                  label="Relationship"
                  hint={relationshipTo === 'inactive'
                    ? 'The Reason below is recorded as the inactive reason on the client master and in its history.'
                    : relationshipTo === 'active'
                      ? `Currently inactive${pickedEntity?.inactiveDate ? ` since ${formatDate(pickedEntity.inactiveDate)}` : ''} — approval reactivates the company.`
                      : undefined}
                >
                  <SegmentedSwitch
                    ariaLabel="Relationship"
                    options={[{ key: 'active', label: 'Active' }, { key: 'inactive', label: 'Inactive' }]}
                    value={relationship}
                    onChange={setRelationship}
                  />
                  {relationshipTo === 'inactive' && (
                    <div className="mt-3 max-w-xs">
                      <Field label="Inactive Date" required error={inactiveDateError}>
                        <input type="date" value={inactiveDate} max={firmToday()} onChange={(e) => setInactiveDate(e.target.value)} className={inputCls} />
                      </Field>
                    </div>
                  )}
                  {inactiveBlocked && (
                    <div className="mt-2 border border-amber-200 rounded-lg p-3 bg-amber-50 text-sm text-amber-800">
                      {inProgress} engagement{inProgress === 1 ? ' is' : 's are'} still in progress on this company — it cannot be marked inactive until {inProgress === 1 ? 'it is' : 'they are'} closed (Engagement Status Change, or Management → Engagements).
                    </div>
                  )}
                </Field>
              </div>
              {currentGroupName ? (
                <div className="md:col-span-2">
                  <Field
                    label="Client Group Name"
                    hint={renameTo
                      ? `Currently "${currentGroupName}" — the group is renamed in place; sister companies, structure charts and appointment letters stay attached, engagements pick up the new name on approval.`
                      : 'Rename the group this company belongs to — e.g. it was registered under its own name (A1) and the group is now serviced as A.'}
                  >
                    <input
                      type="text"
                      value={groupName}
                      onChange={(e) => setGroupName(e.target.value)}
                      className={inputCls}
                    />
                  </Field>
                </div>
              ) : (
                <p className="md:col-span-2 text-sm text-slate-500">This company is not in a client group — there is no group name to change.</p>
              )}
            </div>
          ) : (
            <p className="text-sm text-slate-500">Select a client entity above first.</p>
          )}
        </FormSection>

        <FormSection step={3} title="Attachments">
          <Field label="Supporting Files" hint="e.g. BizFile or other evidence for the reviewer. PDF / DOC / DOCX / JPG / PNG, up to 30 MB per file.">
            <FilePicker
              multiple
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
              files={files}
            />
          </Field>
        </FormSection>

        <FormSection step={4} title="Reason">
          <Field label="Reason" required field="reason">
            <textarea rows="2" value={reason} onChange={(e) => setReason(e.target.value)} className={inputCls} />
          </Field>
        </FormSection>

        <FormSection step={5} title="Reviewer">
          <Field label="Reviewer" required field="reviewerName" hint={MASTER_REVIEWER_HINT}>
            <ReviewerSelect roster="master" value={reviewerStaffId} onChange={setReviewerStaffId} />
          </Field>
        </FormSection>
      </FormValidationProvider>
    </FormShell>
  );
};

export default WorkflowCarNew;
